import { ReactNode } from 'react';

import FormControl from '@mui/material/FormControl';
import classNames from 'classnames';

import { Typography } from '../../Typography';
import { Box } from '../../Box';
import { RadioInput } from '..';
import { Label } from '../common';

import useStyles from './MultipleRadioInput.styles';

/**
 * Свойства компонента {@link MultipleRadioInput}
 */
interface MultipleTextInputProps {
  /**
   * Атрибут name для полей радиокнопок.
   */
  name: string;

  /**
   * Текущее значение группы радиокнопок.
   */
  groupValue: 'all' | 'none' | 'never' | 'hand';

  /**
   * Массив текущих значений радиокнопок.
   */
  values: {
    id: string | number;
    value: 'always' | 'direct_only' | 'never';
  }[];

  /**
   * Метка для группы радиокнопок.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Массив опций для отдельных радиокнопок.
   */
  options: { label: string; id: string | number; disabled?: boolean }[];

  /**
   * Метка для опции 'true' в отдельных радиокнопках.
   */
  trueLabel: string;

  /**
   * Метка для опции 'false' в отдельных радиокнопках.
   */
  falseLabel: string;

  /**
   * Необязательная метка для опции 'never' в отдельных радиокнопках.
   */
  neverLabel?: string;

  /**
   * Метка для опции 'all' в групповой радиокнопке.
   */
  allTrueLabel?: string;

  /**
   * Метка для опции 'none' в групповой радиокнопке.
   */
  allFalseLabel?: string;

  /**
   * Метка для опции 'never' в групповой радиокнопке.
   */
  allNeverLabel?: string;

  /**
   * Метка для опции 'hand' в групповой радиокнопке.
   */
  allSplitLabel?: string;

  /**
   * Функция обратного вызова, которая вызывается при изменении значения групповой радиокнопки.
   */
  onChangeForAllElements: (optionValue: string | number) => void;

  /**
   * Функция обратного вызова, которая вызывается при изменении значения отдельной радиокнопки.
   */
  onChangeForElement: (
    id: string | number,
  ) => (optionValue: string | number) => void;

  /**
   * Функция обратного вызова, которая вызывается, когда радиокнопка получает фокус.
   */
  onFocus?: () => void;

  /**
   * Функция обратного вызова, которая вызывается, когда радиокнопка теряет фокус.
   */
  onBlur?: () => void;

  /**
   * Необязательная всплывающая подсказка для отображения с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Указывает, является ли группа радиокнопок обязательной.
   */
  required?: boolean;

  /**
   * Указывает, отключена ли группа радиокнопок.
   */
  disabled?: boolean;

  /**
   * Указывает на наличие групповых радиокнопок.
   */
  withoutGroup?: boolean;
}

/**
 * Компонент MultipleRadioInput представляет собой настраиваемую группу радиокнопок, которая позволяет использовать несколько радиокнопок.
 * Он поддерживает необязательные метки, всплывающие подсказки, обработку ошибок и отключение состояния.
 *
 * @public
 * @param name - Атрибут name для полей радиокнопок.
 * @param groupValue - Текущее значение группы радиокнопок.
 * @param values - Массив текущих значений радиокнопок.
 * @param label - Метка для группы радиокнопок.
 * @param labelIcon - Иконка для метки.
 * @param options - Массив опций для отдельных радиокнопок.
 * @param trueLabel - Метка для опции 'true' в отдельных радиокнопках.
 * @param falseLabel - Метка для опции 'false' в отдельных радиокнопках.
 * @param neverLabel - Необязательная метка для опции 'never' в отдельных радиокнопках.
 * @param allTrueLabel - Метка для опции 'all' в групповой радиокнопке.
 * @param allFalseLabel - Метка для опции 'none' в групповой радиокнопке.
 * @param allNeverLabel - Метка для опции 'never' в групповой радиокнопке.
 * @param allSplitLabel - Метка для опции 'hand' в групповой радиокнопке.
 * @param onChangeForAllElements - Функция обратного вызова, которая вызывается при изменении значения групповой радиокнопки.
 * @param onChangeForElement - Функция обратного вызова, которая вызывается при изменении значения отдельной радиокнопки.
 * @param onFocus - Функция обратного вызова, которая вызывается, когда радиокнопка получает фокус.
 * @param onBlur - Функция обратного вызова, которая вызывается, когда радиокнопка теряет фокус.
 * @param tooltip - Необязательная всплывающая подсказка для отображения с меткой.
 * @param required - Указывает, является ли группа радиокнопок обязательной.
 * @param disabled - Указывает, отключена ли группа радиокнопок.
 * @param withoutGroup - Флаг отсутсвтия групповых радиокнопок.
 * @returns Отрендеренный компонент MultipleRadioInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleTextInputProps}
 */
export const MultipleRadioInput = ({
  disabled = false,
  values,
  label,
  labelIcon,
  tooltip = null,
  groupValue,
  options,
  trueLabel,
  falseLabel,
  neverLabel,
  allTrueLabel = `Везде ${trueLabel.toLowerCase()}`,
  allFalseLabel = `Везде ${falseLabel.toLowerCase()}`,
  allNeverLabel = `Не согласует ${neverLabel?.toLowerCase()}`,
  allSplitLabel = 'Ручной выбор',
  onChangeForAllElements,
  onChangeForElement,
  required = false,
  withoutGroup = false,
  ...rest
}: MultipleTextInputProps) => {
  const { classes } = useStyles();

  return (
    <FormControl
      className={classNames(
        classes.multipleRadioInput,
        'aqa_multiple_radion_input',
      )}
    >
      {label && (
        <Label
          label={label}
          tooltip={tooltip}
          required={required}
          icon={labelIcon}
        />
      )}

      {!withoutGroup && (
        <Box pb="20">
          <RadioInput
            {...rest}
            row
            value={groupValue}
            disabled={disabled}
            items={[
              { label: allTrueLabel, value: 'all' },
              { label: allFalseLabel, value: 'none' },
              ...(neverLabel ? [{ label: allNeverLabel, value: 'never' }] : []),
              { label: allSplitLabel, value: 'hand' },
            ]}
            onChange={onChangeForAllElements}
          />
        </Box>
      )}
      <Box border={1} borderColor="stroke.primary" radius="l" px="16">
        {options.map(
          ({ id, label: optionLabel, disabled: disabledOption }, index) => (
            <Box
              py="8"
              borderTop={0}
              borderRight={0}
              borderLeft={0}
              borderBottom={index === options.length - 1 ? 0 : 1}
              key={id}
              borderColor="stroke.primary"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="24"
            >
              <Typography variant="body2">{optionLabel}</Typography>
              <Box flexShrink={0} py="8">
                <RadioInput
                  {...rest}
                  row
                  disabled={disabled || disabledOption}
                  value={String(
                    values
                      ? values.find((opt) => opt.id === id)?.value || false
                      : false,
                  )}
                  items={[
                    { label: trueLabel, value: 'always' },
                    { label: falseLabel, value: 'direct_only' },
                    ...(neverLabel
                      ? [{ label: neverLabel, value: 'never' }]
                      : []),
                  ]}
                  onChange={onChangeForElement(id)}
                />
              </Box>
            </Box>
          ),
        )}
      </Box>
    </FormControl>
  );
};
